//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { yupErc20Wei } from './Enums'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountLenderCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'
import { StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { convertValuesToDecStr, convertYupSchemaValuesToDecStr } from './schemaUtils'

export const PoolAccountOnlyCTOSchemaValues: Record<string, yup.Schema> = {
  earlyWithdrawInterestAdjustment: yupErc20Wei.label('Early Withdraw Interest Adjustment').meta(
    toFieldMetaData({
      propName: 'earlyWithdrawInterestAdjustment',
      description: 'Interest earned adjustment due to early withdraw ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  maxWithdrawRequest: yupErc20Wei.label('Max Withdrawal Request').meta(
    toFieldMetaData({
      propName: 'maxWithdrawRequest',
      description:
        'The maximum amount of assets that can be requested for withdrawal. This is calculated multiplying the vault token balance by the exchange rate at maturity less any outstanding withdrawals, including early withdrawals and rollover terminations. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  maxRedeemRequest: yupErc20Wei.label('Max Token Redemption Request').meta(
    toFieldMetaData({
      propName: 'maxRedeemRequest',
      description:
        'The maximum amount of vault tokens that can be requested for withdrawal. This is the vault token balance less any outstanding withdrawal requests, including early withdrawals and rollover terminations.',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),

  maxRequestCancellation: yupErc20Wei.label('Max Request Cancellation ').meta(
    toFieldMetaData({
      propName: 'maxRequestCancellation',
      description: '',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),

  maxWithdraw: yupErc20Wei.label('Maximum Withdrawal Amount').meta(
    toFieldMetaData({
      propName: 'maxWithdraw',
      description: 'Same as withdrawableAssetsOf',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  maxRedeem: yupErc20Wei.label('Max Number of Tokens Redeemable').meta(
    toFieldMetaData({
      propName: 'maxRedeem',
      description: 'Same as redeemableSharesOf',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),

  requestedSharesOf: yupErc20Wei.label('Requested Shares of Account').meta(
    toFieldMetaData({
      propName: 'requestedSharesOf',
      description: 'The amount of vault tokens included in rollover termination requests. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  redeemableSharesOf: yupErc20Wei.label('Redeemable Shares of Account').meta(
    toFieldMetaData({
      propName: 'redeemableSharesOf',
      description:
        'The maximum amount of vault tokens that can currently be redeemed for assets immediately. This equals the requestedSharesOf multiplied by the exchange rate at maturity once the loan has been repaid into the loan vault by the Borrower.',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  requestedAssetsOf: yupErc20Wei.label('(Removed Total) Rollover Terminations Outstanding').meta(
    toFieldMetaData({
      propName: 'requestedAssetsOf',
      description:
        'The amount of assets included in rollover termination requests that have yet to be withdrawn. This is calculated by multiplying the requestedSharesOf by the exchange rate at maturity. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  requestedPrincipalOf: yupErc20Wei.label(' Rollover Terminations Prinipal Outstanding').meta(
    toFieldMetaData({
      propName: 'requestedPrincipalOf',
      description:
        'The amount of assets included in rollover termination requests that have yet to be withdrawn. This is calculated by multiplying the requestedSharesOf by the exchange rate at time of loan. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  withdrawableAssetsOf: yupErc20Wei.label('Withdrawable Assets of Account').meta(
    toFieldMetaData({
      propName: 'withdrawableAssetsOf',
      description:
        'The maximum amount of assets that can currently be withdrawn. This equals the requestedAssetsOf multiplied by the exchange rate at maturity once the loan has been repaid into the loan vault by the Borrower. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  earlyRequestedSharesOf: yupErc20Wei.label('earlyRequestedSharesOf').meta(
    toFieldMetaData({
      propName: 'earlyRequestedSharesOf',
      description:
        'The amount of vault tokens included in all outstanding early withdrawals requests that have not been accepted. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  earlyRequestedAssetsOf: yupErc20Wei.label('Total Early Withdrawals Requested at Maturity').meta(
    toFieldMetaData({
      propName: 'earlyRequestedAssetsOf',
      description:
        'The total amount of early withdrawals requests that have not yet been accepted expressed as their value if held to maturity. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) that have been requested but not accepted (“earlyRequestedSharesOf”) by the exchange rate at maturity (“exchangeRateAtMaturity”). ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  earlyRequestedPrincipalOf: yupErc20Wei.label('*** Total Early Withdrawals Principal Requested').meta(
    toFieldMetaData({
      propName: 'earlyRequestedPrincipalOf',
      description:
        'The total amount of loan principal in early withdrawals requests that have not yet been accepted. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) that have been requested but not accepted (“earlyRequestedSharesOf”) by the exchange rate at the start of the loan cycle. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  earlyAcceptedSharesOf: yupErc20Wei.label('earlyAcceptedSharesOf').meta(
    toFieldMetaData({
      propName: 'earlyAcceptedSharesOf',
      description:
        'The total amount of vault tokens in early withdrawals requests that have been accepted but not yet repaid. This amount decreases when early withdrawals are repaid. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  earlyAcceptedAssetsOf: yupErc20Wei.label('earlyAcceptedAssetsOf').meta(
    toFieldMetaData({
      propName: 'earlyAcceptedAssetsOf',
      description:
        'The total amount of assets in early withdrawals requests that have been accepted but not yet repaid. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) (“earlyAcceptedSharesOf”) by the exchange rate at maturity. This amount decreases when early withdrawals are repaid. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  earlyAcceptedPrincipalOf: yupErc20Wei.label('earlyAcceptedPrincipalOf').meta(
    toFieldMetaData({
      propName: 'earlyAcceptedPrincipalOf',
      description:
        'The total amount of vault tokens in early withdrawals requests that have been accepted but not yet repaid. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) (“earlyAcceptedSharesOf”) by the exchange rate at the start of the loan cycle. This value is included in principalTransferringOut.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',
      description:
        'The amount of assets currently earning finalAPR. For rollover terminations, the rollover termination amount (“requestedAssetsOf”) is included in this value until the maturity date. For early withdrawals, principalEarningInterest is reduced when the early withdrawal is accepted. It is reduced by the amount of loan principal in accepted early withdrawal requests (“earlyAcceptedPrincipalOf”)',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  assetsTransferringIn: yupErc20Wei.label('Assets Transferring In').meta(
    toFieldMetaData({
      propName: 'assetsTransferringIn',
      description:
        'The current amount of Assets that have been loaned and will be included in the next loan cycle, but are not currently earning interest.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  assetsTransferringOut: yupErc20Wei.label('Assets Transferring Out').meta(
    toFieldMetaData({
      propName: 'assetsTransferringOut',
      description:
        'The current amount of Assets currently transferring out, expressed as their value if held to maturity. This value is the sum of requestedAssetsOf once the loan reaches maturity and earlyAcceptedAssetsOf. Note: Rollover terminations will be included in this amount once the loan reaches maturity and the funds begin the transfer out process and will decrease when assets are withdrawn from the vault. For Early Withdrawals, earlyAcceptedAssetsOf is included in this value as soon as the early withdrawal request is accepted and will decrease as soon as the Early Withdrawal is repaid.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  principalTransferringOut: yupErc20Wei.label('*** Principal Transferring Out').meta(
    toFieldMetaData({
      propName: 'principalTransferringOut',
      description:
        'The amount of loan principal in the transfer out process. This value does not include any accrued interest or expected interest. For rollover terminations, this amount will increase when the loan reaches maturity with outstanding rollover terminations and decrease when the Lender redeems vault tokens and receives USDC. For early withdrawal requests, this amount will increase when the early withdrawal requests are accepted and decrease when the early withdrawal is repaid. Calculated as requestedSharesOf at loan maturity, multiplied by the exchange rate at start plus earlyAcceptedSharesOf multiplied by the exchange rate at start ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  interestAccruedInActiveLoan: yupErc20Wei.label('Interest Accrued in Active Loan').meta(
    toFieldMetaData({
      propName: 'interestAccruedInActiveLoan',
      description:
        'The current amount of interest accrued in the current loan cycle based on the principalEarningInterest',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  expectedInterestAtMaturity: yupErc20Wei.label('Expected Interest At Maturity').meta(
    toFieldMetaData({
      propName: 'expectedInterestAtMaturity',
      description:
        'The expected amount of interest to be accrued in the current loan cycle based on the principalEarningInterest and finalAPR',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  totalAssetsDeposited: yupErc20Wei.label('All Assets Loaned in pool').meta(
    toFieldMetaData({
      propName: 'totalAssetsDeposited',
      description: 'The total amount of USDC that the account has loaned since vault inception. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  totalAssetsWithdrawn: yupErc20Wei.label('All Assets Withdrawn From Pool').meta(
    toFieldMetaData({
      propName: 'totalAssetsWithdrawn',
      description: 'The total amount of USDC that the account has been withdrawn since vault inception. ',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  totalAssetsAtMaturity: yupErc20Wei.label('Value of Assets at Maturity').meta(
    toFieldMetaData({
      propName: 'totalAssetsAtMaturity',
      description:
        'The total value of vault tokens the account holds, which is calculated by multiplying the vault token balance by exchange rate at maturity. This equals the sum of principalEarningInterest, expectedInterestAtMaturity, and assetsTransferringIn.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  totalAssetCurrent: yupErc20Wei.label(' *** Assets Current Value prorated to the maturity date').meta(
    toFieldMetaData({
      propName: 'totalAssetCurrent',
      description:
        'The total value of vault tokens the account holds currently, which is calculated by multiplying the vault token balance by the prorated exchange rate. This equals the sum of principalEarningInterest, interestAccruedinActiveLoan, and assetsTransferringIn. Please note that early withdrawals are not guaranteed to pay the interestAccruedinActiveLoan as the early withdrawal amount will be determined by market prices when loan collateral is liquidated. ',

      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  interestInDay: yupErc20Wei.label('Interest on Day').meta(
    toFieldMetaData({
      propName: 'interestInDay',
      description: 'Interest earned in the day',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const PoolAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolAccountOnlyCTOSchemaValues,
  })
  .label('PoolAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolAccountCTO', description: 'Pool Account CTO' }))

export const PoolTermAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolTermAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolTermAccountSummaryCTO',
      map: 'PoolAccountCTO',
      description: 'Pool Term Account CTO',
    })
  )

export const PoolAccountOnlyCTOSchema = yup.object({
  ...PoolAccountOnlyCTOSchemaValues,
})

export const PoolAccountTTOOnlySchema = yup
  .object({
    liquidityTokenSymbol: yup
      .string()
      .label('liquidityTokenSymbol')
      .meta(toFieldMetaData({ propName: 'liquidityTokenSymbol', stringType: StringTypeEnum.SYMBOL })),
  })
  .concat(yup.object(convertYupSchemaValuesToDecStr(PoolAccountCTOSchema)))
  .concat(PoolAccountCTOSchema)

export interface PoolAccountSummaryCTO extends InferType<typeof PoolTermAccountSummaryCTOSchema> {}

export interface PoolTermAccountSummaryCTO extends InferType<typeof PoolTermAccountSummaryCTOSchema> {}
export interface PoolAccountCTO extends PoolBaseAccountCTO, InferType<typeof PoolAccountOnlyCTOSchema> {
  earlyWithdrawInterestAdjustment: ERC20Wei
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  maxRequestCancellation: ERC20Wei
  maxWithdraw: ERC20Wei
  maxRedeem: ERC20Wei
  requestedSharesOf: ERC20Wei
  redeemableSharesOf: ERC20Wei
  requestedPrincipalOf: ERC20Wei
  requestedAssetsOf: ERC20Wei
  withdrawableAssetsOf: ERC20Wei
  earlyRequestedSharesOf: ERC20Wei
  earlyRequestedAssetsOf: ERC20Wei
  earlyRequestedPrincipalOf: ERC20Wei
  earlyAcceptedSharesOf: ERC20Wei
  earlyAcceptedAssetsOf: ERC20Wei
  earlyAcceptedPrincipalOf: ERC20Wei
  principalEarningInterest: ERC20Wei
  assetsTransferringIn: ERC20Wei
  assetsTransferringOut: ERC20Wei
  principalTransferringOut: ERC20Wei
  interestAccruedInActiveLoan: ERC20Wei
  expectedInterestAtMaturity: ERC20Wei
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei
  totalAssetsAtMaturity: ERC20Wei
  totalAssetCurrent: ERC20Wei
  interestInDay: ERC20Wei
}

export function PoolLenderReportingAccountCTOSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolBaseAccountLenderCTOSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolBaseAccountLenderCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolBaseAccountLenderCTOSchemaValues[prop]
      }
      // if (PoolFlexAccountOnlyCTOSchemaValues[prop]) {
      //   schemaValues[prop] = PoolFlexAccountOnlyCTOSchemaValues[prop]
      // }
      // if (PoolAccountOnlyCTOSchemaValues[prop]) {
      //   schemaValues[prop] = PoolAccountOnlyCTOSchemaValues[prop]
      // }
      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}

export interface PoolAccountTTO extends PoolAccountCTO, InferType<typeof PoolAccountTTOOnlySchema> {}

export function toPoolAccountTTO(poolAccountCTO: PoolAccountCTO): PoolAccountTTO {
  return {
    ...poolAccountCTO,
    ...convertValuesToDecStr(PoolAccountCTOSchema, poolAccountCTO),
  }
}
